import { onMounted, onBeforeUnmount } from "vue";

export default function useFocusTrap(
  containerRef: { value: HTMLElement | null },
  onEscape?: () => void
) {
  function isSameElement(el1: Element | null, el2: Element): boolean {
    if (!el1) return false;

    if (el1 instanceof HTMLInputElement && el2 instanceof HTMLInputElement) {
      return el1.id === el2.id;
    }

    return (
      el1.getAttribute("class") === el2.getAttribute("class") &&
      el1.textContent?.trim() === el2.textContent?.trim()
    );
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape" && onEscape) {
      event.preventDefault();
      onEscape();
    }

    if (event.key !== "Tab") return;

    if (!containerRef.value) return;

    const focusableElements = containerRef.value.querySelectorAll<HTMLElement>(
      'button, [tabindex="0"], select, input'
    );

    if (focusableElements.length === 0) return;

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
      focusableElements[focusableElements.length - 1];

    if (
      event.shiftKey &&
      isSameElement(document.activeElement, firstFocusableElement)
    ) {
      event.preventDefault();
      lastFocusableElement.focus();
    } else if (
      !event.shiftKey &&
      isSameElement(document.activeElement, lastFocusableElement)
    ) {
      event.preventDefault();
      firstFocusableElement.focus();
    }
  }

  onMounted(() => {
    if (containerRef.value) {
      containerRef.value.addEventListener("keydown", handleKeyDown);
    }
  });

  onBeforeUnmount(() => {
    if (containerRef.value) {
      containerRef.value.removeEventListener("keydown", handleKeyDown);
    }
  });
}
